/**
 * L229-WEB — Support System Phase 1 shared client constants.
 *
 * Severity helper text + module catalog are copied VERBATIM from
 * docs/superpowers/specs/2026-07-19-support-system-phase1-design.md (§1, §2).
 * Do not reword — mobile/CPA carry the same strings.
 *
 * SUPPORT_DESK_ROLES mirrors the backend's exported SUPPORT_DESK_ROLES
 * (l229-be lane) — the tenant's existing admin/owner gates. This is a
 * UI-honesty gate only; the backend stays authoritative (non-desk roles get
 * own-only lists server-side regardless of what the client renders).
 */

/** Roles that see the Support Desk (all tickets + patch/assign). */
export const SUPPORT_DESK_ROLES = ['admin', 'owner', 'super_admin'];

export function isSupportDeskRole(roleName: string | null | undefined): boolean {
  return SUPPORT_DESK_ROLES.includes((roleName ?? '').toLowerCase().trim());
}

/** Reporter-facing severity options. `critical` is deliberately absent —
 *  it remains a legal DB value for admin-side escalation only (spec §1). */
export const SEVERITY_OPTIONS: Array<{
  value: 'high' | 'medium' | 'low';
  label: string;
  helper: string;
}> = [
  { value: 'high', label: 'High', helper: "Blocking work — you can't proceed." },
  { value: 'medium', label: 'Medium', helper: "There's a workaround, but it's taking time and affecting productivity." },
  { value: 'low', label: 'Low', helper: 'Small UI issue — non-blocking.' },
];

export const CATEGORY_OPTIONS: Array<{ value: 'bug' | 'feature_request'; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature_request', label: 'Feature request' },
];

/** Module catalog — values verbatim from spec §2. */
export const SUPPORT_MODULES: Array<{ value: string; label: string }> = [
  { value: 'leads', label: 'Leads' },
  { value: 'calls', label: 'Calls' },
  { value: 'triage', label: 'Triage' },
  { value: 'calculators', label: 'Calculators' },
  { value: 'proposals', label: 'Proposals' },
  { value: 'sales_orders', label: 'Sales Orders' },
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'hr_attendance', label: 'HR / Attendance' },
  { value: 'dealers', label: 'Dealers' },
  { value: 'other', label: 'Other' },
];

export function moduleLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return SUPPORT_MODULES.find((m) => m.value === value)?.label ?? value;
}

/** Attachment caps — mirror the server's 400-on-violation rules. */
export const MAX_ATTACHMENTS = 3;
export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // 2 MB per attachment

export interface SupportAttachment {
  name: string;
  dataUrl: string;
}

export const SOURCE_LABELS: Record<string, string> = {
  web: 'Web',
  sales_app: 'Sales app',
  triage_app: 'Triage app',
  partner_app: 'Partner app',
};
